import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { QrCode, Package2 } from 'lucide-react';
import { PalletSummaryDto } from '../../../../types/picking';
import { parseGS1Barcode as parseGS1BarcodeFull } from '../../../../utils/gs1Parser';

// Extended interface to include batch and expiry data
export interface GS1ScanData {
  itemNo?: string;
  quantity?: number;
  sscc?: string;
  batchNo?: string;
  expiryDate?: string;
  gtin?: string;
}

interface GS1ScannerProps {
  onScan: (data: GS1ScanData) => void;
  pallets: PalletSummaryDto[];
  activePallet: PalletSummaryDto | undefined;
  onSetActivePallet: (sscc: string) => void;
  onCreatePallet: (ssccCode?: string, gs1Data?: GS1ScanData) => void;
  onCompletePicking: () => void;
  onSendToPacking: () => void;
  /** When true, scans are treated as location barcodes instead of item barcodes */
  isLocationScanOnlyMode?: boolean;
  /** Callback for location scan in location-scan-only mode */
  onLocationScan?: (barcode: string) => void;
}

export interface GS1ScannerRef {
  focusInput: () => void;
}

export const GS1Scanner = forwardRef<GS1ScannerRef, GS1ScannerProps>(({
  onScan,
  pallets,
  activePallet,
  onSetActivePallet,
  onCreatePallet,
  onCompletePicking,
  onSendToPacking,
  isLocationScanOnlyMode,
  onLocationScan
}, ref) => {
  const { t } = useTranslation();
  const [scanInput, setScanInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Expose focus function to parent component
  useImperativeHandle(ref, () => ({
    focusInput: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }));

  // Auto-focus disabled - global scanning is active
  // useEffect(() => {
  //   if (inputRef.current) {
  //     inputRef.current.focus();
  //   }
  // }, []);

  // Use the comprehensive GS1 parser from utils
  const parseAndExtractGS1Data = (barcode: string): GS1ScanData => {
    // Use the robust parser from gs1Parser.ts
    const fullGS1Data = parseGS1BarcodeFull(barcode);

    console.log('📦 Full GS1 parsed data:', fullGS1Data);

    // Extract relevant fields for picking
    return {
      sscc: fullGS1Data.sscc,
      gtin: fullGS1Data.gtin,
      itemNo: fullGS1Data.gtin, // Use GTIN as itemNo (matching handles cleaning)
      quantity: fullGS1Data.quantity,
      batchNo: fullGS1Data.batchNo,
      expiryDate: fullGS1Data.expiryDate || fullGS1Data.bestBeforeDate || fullGS1Data.useByDate
    };
  };

  const handleScanItem = () => {
    if (!scanInput) return;

    // Prevent double-scanning - ignore new scans while processing
    if (isProcessing) {
      console.log('⏸️ GS1Scanner: Already processing, ignoring scan');
      return;
    }

    setIsProcessing(true);
    console.log('🔍 GS1Scanner: Starting scan with input:', scanInput);

    // In location-scan-only mode, route directly to location handler
    if (isLocationScanOnlyMode && onLocationScan) {
      const cleanedBarcode = scanInput.replace(/[~§]+$/g, '').trim();
      console.log('📍 GS1Scanner: Location-scan-only mode, routing to location handler:', cleanedBarcode);
      onLocationScan(cleanedBarcode);
      setScanInput('');
      setTimeout(() => setIsProcessing(false), 100);
      return;
    }

    // First, remove trailing scanner separators to check for real GS1 structure
    const withoutTrailingSeparators = scanInput.replace(/[~§]+$/g, '').trim();

    // Check if this looks like a GS1-128 structured barcode
    // GS1-128 has separators WITHIN the string (not just trailing), or ]C1 prefix
    const hasInternalSeparators = withoutTrailingSeparators.includes('~') ||
                                   withoutTrailingSeparators.includes('§') ||
                                   withoutTrailingSeparators.includes(String.fromCharCode(29));
    const hasParentheses = scanInput.includes('(');
    const hasC1Prefix = scanInput.startsWith(']C1');

    // A real GS1 barcode has structure indicators, not just trailing separators from scanner
    const looksLikeGS1 = hasInternalSeparators || hasParentheses || hasC1Prefix;

    // Try GS1 parsing if it has GS1 structure indicators
    let fullGS1Data = null;
    let hasGS1Data = false;

    if (looksLikeGS1) {
      console.log('🔍 GS1 structure detected, attempting GS1 parse...');
      fullGS1Data = parseGS1BarcodeFull(scanInput);
      hasGS1Data = !!(fullGS1Data.parsedAIs && Object.keys(fullGS1Data.parsedAIs).length > 0);
      console.log('📊 GS1 parse result - hasGS1Data:', hasGS1Data, 'parsedAIs:', fullGS1Data?.parsedAIs);
    } else {
      console.log('📝 No GS1 structure detected, treating as plain barcode');
    }

    if (hasGS1Data) {
      console.log('📊 GS1Scanner: Detected GS1-128 barcode');
      const gs1Data = parseAndExtractGS1Data(scanInput);
      console.log('📋 GS1Scanner: Extracted data:', gs1Data);

        // Determine what type of scan this is
        const hasSSCC = !!gs1Data.sscc;
        const hasItem = !!(gs1Data.itemNo || gs1Data.gtin);

        // If no relevant GS1 data (no SSCC and no Item), treat as plain barcode
        if (!hasSSCC && !hasItem) {
          console.log('⚠️ GS1 detected but no SSCC/GTIN found - treating as plain barcode');
          // Strip trailing scanner separators from plain barcode
          const cleanedItemNo = scanInput.replace(/[~§]+$/g, '').trim();
          onScan({ itemNo: cleanedItemNo });
        }
        // PRIORITY 1: If both SSCC and Item are present, this is a pre-filled pallet scan
        else if (hasSSCC && hasItem) {
          console.log('📦 Pre-filled pallet scan detected (SSCC + Item)');
          // Send to parent - let parent handle both pallet creation AND item registration
          onScan(gs1Data);
        }
        // PRIORITY 2: SSCC only - pallet activation/creation
        else if (hasSSCC && !hasItem) {
          console.log('📦 SSCC-only scan - pallet management');
          let targetPallet = pallets.find(p => p.sscc === gs1Data.sscc);

          if (!targetPallet) {
            console.log('➕ Creating empty pallet with SSCC:', gs1Data.sscc);
            onCreatePallet(gs1Data.sscc);
          } else {
            console.log('✅ Activating existing pallet:', targetPallet.sscc);
            onSetActivePallet(targetPallet.sscc);
          }
        }
        // PRIORITY 3: Item only (no SSCC) - item validation/scanning
        else if (!hasSSCC && hasItem) {
          console.log('🔎 Item-only scan (validation/picking)');
          onScan(gs1Data);
        }

      setScanInput('');

      // Allow next scan after a short delay to ensure UI has updated
      setTimeout(() => setIsProcessing(false), 100);
      return;
    }

    // Not GS1-128 or parsing failed - treat as plain barcode
    console.log('📝 GS1Scanner: Plain barcode (not GS1-128)');
    // Strip trailing scanner separators (~ and §) from plain barcodes
    const cleanedItemNo = scanInput.replace(/[~§]+$/g, '').trim();
    console.log('🧹 Cleaned plain barcode:', cleanedItemNo);
    // Plain item scan - just item number (matching logic handles GTIN cleaning)
    onScan({ itemNo: cleanedItemNo });

    setScanInput('');

    // Allow next scan after a short delay to ensure UI has updated
    setTimeout(() => setIsProcessing(false), 100);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={onCompletePicking}
          className="px-4 py-2.5 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
        >
          {t('warehouse.modules.picking.actions.completePick')}
        </button>
        <button
          onClick={onSendToPacking}
          className="px-4 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          {t('warehouse.modules.picking.actions.sendToPacking')}
        </button>
      </div>

      {/* Scanner Input */}
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          placeholder={isLocationScanOnlyMode ? 'Skann lokasjon...' : t('warehouse.modules.picking.placeholders.scanBarcode')}
          className="flex-1 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          value={scanInput}
          onChange={(e) => setScanInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleScanItem()}
          disabled={isProcessing}
        />
        <button
          onClick={handleScanItem}
          disabled={isProcessing}
          className={`p-2.5 rounded-lg ${
            isProcessing
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          } text-white`}
          title={t('common.scan')}
        >
          <QrCode size={20} />
        </button>
      </div>
    </div>
  );
});
